import {
  Placement,
  Strategy,
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';

import React, { cloneElement, useState, type JSX } from 'react';
import ReactDOM from 'react-dom';

export interface TooltipProps {
  label: string | React.ReactNode;
  placement?: Placement;
  children: JSX.Element;
  strategy?: Strategy;
  defaultOpen?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /**
   * Whether the tooltip describes its element to screen readers. Turn it off
   * when the element's own name already carries the text, so it isn't read
   * twice.
   */
  describesReference?: boolean;
}

const getElem = () => {
  let elem = document.getElementById('pl-tooltip');
  if (!elem) {
    elem = document.createElement('div');
    elem.id = 'pl-tooltip';
    document.body.appendChild(elem);
  }
  return elem;
};

export const Tooltip = ({
  children,
  label,
  strategy = 'fixed',
  placement = 'top',
  defaultOpen = false,
  className,
  style = {},
  describesReference = true,
}: TooltipProps) => {
  const [open, setOpen] = useState(defaultOpen);

  const { x, y, refs, context } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    middleware: [offset(5), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    strategy,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context),
    useFocus(context),
    useRole(context, { role: 'tooltip', enabled: describesReference }),
    useDismiss(context),
  ]);

  const tooltip = (
    <div
      {...getFloatingProps({
        ref: refs.setFloating,
        ...(describesReference ? {} : { role: 'tooltip' }),
        // A ring rather than a border, so the edge adds no size.
        className: `bg-tooltip-background ring-tooltip-border text-white ring-1 dark:shadow-lg ${className ?? ''}`,
        style: {
          position: strategy,
          top: y ?? '',
          left: x ?? '',
          padding: '1px 6px',
          paddingBottom: '2px',
          boxSizing: 'border-box',
          borderRadius: '4px',
          zIndex: 99999999999999,
          fontSize: '14px',
          display: 'inline-flex',
          alignItems: 'center',
          ...style,
        },
      })}
    >
      <span>{label}</span>
    </div>
  );

  return (
    <>
      {cloneElement(
        children,
        getReferenceProps({ ref: refs.setReference, ...children.props }),
      )}
      {label &&
        open &&
        // Found or created on first show, not on import, so files that only
        // import this (e.g. in tests without a DOM) don't need a document.
        (strategy === 'fixed'
          ? ReactDOM.createPortal(tooltip, getElem())
          : getElem())}
    </>
  );
};
